import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import {
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaTimes,
} from 'react-icons/fa';

import { Select, DatePicker } from '~/components/Form';
import SelectWithFilterActiveCheckbox from '~/components/Form/SelectWithFilterActiveCheckbox';
import Pagination from '~/components/Pagination';
import { TableLoading, TableContainer } from '~/components/Table';

import api from '~/services/api';

import { useAuth } from '~/hooks';
import {
  Container,
  Header,
  Filter,
  Content,
} from '../ListExpirationControl/List/styles';

const Logs = () => {
  const filterRef = useRef(null);

  const { company } = useAuth();
  const [clients, setClients] = useState(null);
  const [clientsOptions, setClientsOptions] = useState([]);
  const [filterClient, setFilterClient] = useState({
    value: '',
    label: 'Todos',
  });
  const [logs, setLogs] = useState([]);
  const [processModelsOptions, setProcessModelsOptions] = useState([
    { value: null, label: 'Todos' },
  ]);
  const [filters, setFilters] = useState({
    processModel_id: null,
    client_id: null,
    start_date: null,
    end_date: null,
    page: 1,
  });
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/processes/logs/expiration-control', {
        params: {
          ...filters,
          key: sortConfig.key,
          direction: sortConfig.direction,
        },
      });

      const { docs, pages, total } = response.data;

      setLogs(
        docs.map(log => ({
          ...log,
          formatted_created_at: format(
            new Date(log.created_at),
            'dd/MM/yyyy HH:mm'
          ),
          formatted_updated_at: format(
            new Date(log.updated_at),
            'dd/MM/yyyy HH:mm'
          ),
        }))
      );
      setTotalDocs(total);
      setTotalPages(pages);
    } catch {
      toast.error('Falha ao carregar os logs.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig]);

  const loadOptions = useCallback(async () => {
    try {
      const processModelsResponse = await api.get('/process-models/simple', {
        params: { company_id: 3, model_type: 2 },
      });

      setProcessModelsOptions(() => [
        { value: null, label: 'Todos' },
        ...processModelsResponse.data.map(model => ({
          value: model.id,
          label: model.title,
        })),
      ]);
    } catch {
      toast.error('Falha ao carregar opções de filtros.', {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilter = useCallback(data => {
    setFilters({
      processModel_id: data.processModel_id || null,
      client_id: data.client ? data.client.value : null,
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      page: 1,
    });
    setFilterClient(data.client || { value: '', label: 'Todos' });
  }, []);

  const handlePage = useCallback(page => {
    setFilters(prevFilters => ({
      ...prevFilters,
      page,
    }));
  }, []);

  const sort = useCallback(
    key => {
      let newSortConfig = { key: null, direction: null };

      // Se já estamos ordenando por esta coluna
      if (sortConfig.key === key) {
        // Ciclo: null (sem ordenação) → desc → asc → null (sem ordenação)
        if (sortConfig.direction === null) {
          newSortConfig = { key, direction: 'desc' };
        } else if (sortConfig.direction === 'desc') {
          newSortConfig = { key, direction: 'asc' };
        }
        // Se direction for 'asc', volta para null (já definido como padrão acima)
      } else {
        // Primeira vez clicando nesta coluna: começar com desc
        newSortConfig = { key, direction: 'desc' };
      }

      setSortConfig(newSortConfig);
    },
    [sortConfig]
  );

  useEffect(() => {
    async function loadClients() {
      try {
        const response = await api.get(`/relationships`, {
          params: {
            company_id: company.id,
            selectOnly: true,
            type: 1,
          },
        });

        if (response.data.length > 0) {
          response.data.sort((a, b) => {
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
            return 0;
          });

          const options = response.data.map(item => ({
            value: item.id,
            label: item.name,
            active: item.active,
          }));

          options.unshift({
            value: '',
            label: 'Todos',
            active: true,
          });

          setClients(options);
          setClientsOptions(options.filter(item => item.active));
        } else {
          toast.warn('Nenhum cliente foi encontrado.', {
            position: toast.POSITION.BOTTOM_RIGHT,
          });
        }
      } catch {
        toast.error('Falha ao buscar clientes.', {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }
    }

    loadClients();
  }, [company]);

  const handleFilterClientsOptions = useCallback(
    value => {
      if (clients) {
        if (value === true) {
          setClientsOptions(clients.filter(clientItem => clientItem.active));
        } else {
          setClientsOptions(clients);
        }
      }
    },
    [clients]
  );

  return (
    <Container>
      <Header>
        <div>
          <h1>Logs - Controle de Vencimento</h1>
        </div>
        <aside>
          <Link to="/expiration-control-models">
            <FaTimes size={20} color="#44546a" />
          </Link>
        </aside>
      </Header>

      <Filter
        ref={filterRef}
        onSubmit={() => {
          const data = filterRef.current.getData();
          handleFilter({
            ...data,
            client: filterClient,
          });
        }}
      >
        <Select
          label="Modelo de Processo"
          name="processModel_id"
          options={processModelsOptions}
          defaultValue={processModelsOptions[0]}
          placeholder="Selecione um modelo de processo"
          className="user"
        />
        <SelectWithFilterActiveCheckbox
          label="Cliente"
          name="client"
          className="user"
          options={clientsOptions}
          value={filterClient}
          handleFilter={handleFilterClientsOptions}
          onChange={e => {
            setFilterClient(e);
          }}
        />
        <DatePicker name="start_date" label="Data Inicial" />
        <DatePicker name="end_date" label="Data Final" />
        <button type="submit">
          <FaSearch />
        </button>
      </Filter>

      {loading ? (
        <TableLoading />
      ) : (
        <Content
          style={{
            overflowX: 'auto',
            maxHeight: '100%',
            height: 'calc(100vh - 300px)',
          }}
        >
          <TableContainer>
            <thead>
              <tr>
                <th style={{ textAlign: 'center' }}>ID</th>
                <th style={{ textAlign: 'left' }}>Cliente</th>
                <th style={{ textAlign: 'left' }}>Orientação</th>
                <th
                  id="created_at"
                  className="created_at sort"
                  onClick={() => sort('created_at')}
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    Criado
                    {(!sortConfig.key || sortConfig.key !== 'created_at') && (
                      <FaSort style={{ marginLeft: '5px' }} />
                    )}
                    {sortConfig.key === 'created_at' &&
                      sortConfig.direction === 'asc' && (
                        <FaSortUp style={{ marginLeft: '5px' }} />
                      )}
                    {sortConfig.key === 'created_at' &&
                      sortConfig.direction === 'desc' && (
                        <FaSortDown style={{ marginLeft: '5px' }} />
                      )}
                  </div>
                </th>
                <th style={{ textAlign: 'left' }}>Quem fez</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id}>
                  <td style={{ textAlign: 'center' }}>{log.id}</td>
                  <td style={{ textAlign: 'left' }}>
                    {log.client
                      ? log.client.nickname || log.client.name || 'Sem nome'
                      : 'Sem nome'}
                  </td>
                  <td style={{ textAlign: 'left' }}>{log.document.document}</td>
                  <td style={{ textAlign: 'center' }}>
                    {log.formatted_created_at}
                  </td>
                  <td style={{ textAlign: 'left' }}>
                    {log.user.nick || log.user.name || 'Sem usuário'}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableContainer>
        </Content>
      )}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: '#fff',
          zIndex: 10,
        }}
      >
        <Pagination
          loading={loading ? 1 : 0}
          currentPage={filters.page}
          pages={totalPages}
          handlePage={handlePage}
          totalDocs={totalDocs}
        />
      </div>
    </Container>
  );
};

export default Logs;
